import { useQuery } from "@tanstack/react-query";
import errorTracker from "~/utils/errorTracker";

import type { MapProvider } from "~/sdks/LocalContent/types";
import type { TravelMode, Location, DistanceUnits, CommuteData } from "./types";

const MAPBOX_DIRECTIONS_ENDPOINT = `${import.meta.env.VITE_PROVIDERS_ENDPOINT}/v1/directions`;
const GOOGLE_DIRECTIONS_ENDPOINT = `${import.meta.env.VITE_GOOGLE_MAPS_API_ENDPOINT}`;

export function useCommute({
  mapProvider,
  mode = "walking",
  origin,
  destination,
  label,
  locale = "en",
  units = "metric",
}: {
  mapProvider?: MapProvider;
  mode?: TravelMode;
  origin?: Location;
  destination?: Location;
  label?: string;
  locale?: string;
  units?: DistanceUnits;
}) {
  const fetchMapboxRoute = async () => {
    if (!origin?.lat || !origin.lng || !destination?.lat || !destination.lng) {
      return null;
    }

    let travelMode = "walking";

    if (mode === "driving") {
      travelMode = "driving";
    } else if (mode === "cycling") {
      travelMode = "cycling";
    }

    return fetch(
      `${MAPBOX_DIRECTIONS_ENDPOINT}/${travelMode}/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?geometries=geojson`,
      {
        headers: {
          Accept: "application/json",
          Authorization: `${import.meta.env.VITE_API_KEY as string}`,
        },
        method: "GET",
      }
    )
      .then((response) => response.json())
      .then((response) => {
        if (
          (response.data?.routes ?? []).length > 0 &&
          response.data?.code === "Ok"
        ) {
          return {
            mode: travelMode,
            distance: response.data.routes[0].distance,
            duration: response.data.routes[0].duration,
            label,
            // Convert coordinates from [lng, lat] to [lat, lng]
            geometry: response.data.routes[0].geometry.coordinates.map(
              (coord: [number, number]) => [coord[1], coord[0]]
            ),
            destination: {
              lat: destination.lat,
              lng: destination.lng,
            },
          } as CommuteData;
        }
        return null;
      })
      .catch((error) => {
        errorTracker.captureException(error);
        throw error;
      });
  };

  const fetchGoogleRoute = async () => {
    if (!origin?.lat || !origin.lng || !destination?.lat || !destination.lng) {
      return null;
    }

    if (typeof mapProvider?.key === "undefined") {
      throw new Error("Google API key is required for Google Maps routes.");
    }

    let travelMode = "WALK";

    if (mode === "driving") {
      travelMode = "DRIVE";
    } else if (mode === "cycling") {
      travelMode = "BICYCLE";
    }

    return fetch(GOOGLE_DIRECTIONS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Goog-Api-Key": mapProvider.key,
        "X-Goog-FieldMask":
          "routes.duration,routes.distanceMeters,routes.localizedValues.duration,routes.polyline.geoJsonLinestring",
      },
      body: JSON.stringify({
        languageCode: locale,
        travelMode,
        origin: {
          location: {
            latLng: {
              latitude: origin.lat,
              longitude: origin.lng,
            },
          },
        },
        destination: {
          location: {
            latLng: {
              latitude: destination.lat,
              longitude: destination.lng,
            },
          },
        },
        units,
        polylineEncoding: "GEO_JSON_LINESTRING",
      }),
    })
      .then((response) => response?.json())
      .then((response) => {
        if (response?.routes?.length > 0) {
          return {
            mode: travelMode,
            distance: response.routes[0]?.distanceMeters ?? null,
            duration: Number(
              (response.routes[0]?.duration as string)?.replace("s", "") ?? 0
            ),
            label,
            geometry:
              response.routes[0].polyline.geoJsonLinestring.coordinates.map(
                (coord: [number, number]) => [coord[1], coord[0]]
              ),
            destination: {
              lat: destination.lat,
              lng: destination.lng,
            },
          } as CommuteData;
        }

        return null;
      })
      .catch((error) => {
        errorTracker.captureException(error);
        return null;
      });
  };

  const fetchRoutes = async () => {
    if (mapProvider?.name === "google") {
      return fetchGoogleRoute();
    }

    return fetchMapboxRoute();
  };

  return useQuery({
    queryKey: [
      "commute",
      mapProvider?.name,
      mapProvider?.key,
      mode,
      origin?.lat,
      origin?.lng,
      destination?.lat,
      destination?.lng,
      locale,
      units,
      label,
    ],
    queryFn: fetchRoutes,
    enabled:
      !!mapProvider?.name &&
      !!origin?.lat &&
      !!origin?.lng &&
      !!destination?.lat &&
      !!destination?.lng,
  });
}
